import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

interface EventLite {
  id: string;
  name: string;
  date: string;
  startTime: string;
  endTime: string;
  venue?: { name: string } | null;
}

// Scheduled event times are stored as Eastern but serialized like UTC — parse
// the wall-clock parts directly (no new Date(iso)) to avoid timezone shift.
function formatTime(timeStr: string): string {
  if (!timeStr) return '';
  let hours: number, minutes: number;
  if (timeStr.includes('T')) {
    const timePart = timeStr.split('T')[1].slice(0, 5);
    [hours, minutes] = timePart.split(':').map(Number);
  } else if (timeStr.includes(':')) {
    [hours, minutes] = timeStr.split(':').map(Number);
  } else {
    return timeStr;
  }
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours % 12 || 12;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

function formatDate(dateStr: string): string {
  if (!dateStr) return '';
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
  const [year, month, day] = datePart.split('-').map(Number);
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}

// Determine the event to feature: one happening now, else the next upcoming, else the most recent.
function pickFeaturedEvent(events: EventLite[]): { event: EventLite | null; label: string } {
  const now = Date.now();
  const withTimes = events
    .filter((e) => e.startTime && e.endTime)
    .map((e) => ({ e, start: new Date(e.startTime).getTime(), end: new Date(e.endTime).getTime() }));

  const happeningNow = withTimes.find((x) => x.start <= now && x.end >= now);
  if (happeningNow) return { event: happeningNow.e, label: 'Happening Now' };

  const upcoming = withTimes.filter((x) => x.start > now).sort((a, b) => a.start - b.start);
  if (upcoming.length > 0) return { event: upcoming[0].e, label: 'Coming Up' };

  return { event: null, label: '' };
}

interface ActionCard {
  to: string;
  icon: string;
  title: string;
  desc: string;
  color: string;
}

const ACTIONS: ActionCard[] = [
  { to: '/scan-station', icon: '📷', title: 'Scan a QR Code', desc: 'Check in a guest by scanning their pass', color: '#1a6b3c' },
  { to: '/packet-pickup', icon: '🔎', title: 'Check In a Guest', desc: 'Search by name, ticket, or order number', color: '#1565c0' },
  { to: '/checkin-station', icon: '🎟️', title: 'Desktop Check-In', desc: 'Full-screen station — keeps working if wifi drops', color: '#0f766e' },
  { to: '/logistics-center', icon: '🚪', title: 'Monitor a Gate', desc: 'Count entries and track entrances', color: '#7b1fa2' },
  { to: '/delivery-board', icon: '📦', title: 'Delivery Board', desc: 'Big-screen view of packet pickups waiting', color: '#b45309' },
  { to: '/events', icon: '📅', title: 'Event Schedule', desc: 'See what is happening and when', color: '#e65100' },
];

export function VolunteerConsolePage() {
  const { user } = useAuth();
  const [events, setEvents] = useState<EventLite[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    apiClient<EventLite[]>('/events')
      .then((data) => { if (!cancelled) setEvents(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const firstName = user?.fullName?.split(' ')[0] || 'Volunteer';
  const { event: featured, label } = pickFeaturedEvent(events);

  return (
    <div className="attendee-page" style={styles.page}>
      {/* Greeting */}
      <div style={styles.header}>
        <h1 style={styles.title}>👋 Welcome, {firstName}</h1>
        <p style={styles.subtitle}>Your volunteer console</p>
      </div>

      {/* Featured event */}
      {loading ? (
        <div style={styles.eventCardLoading}>Loading event…</div>
      ) : featured ? (
        <div style={styles.eventCard}>
          <div style={styles.eventLabel}>{label === 'Happening Now' ? '🔴 Happening Now' : '📅 Coming Up'}</div>
          <div style={styles.eventName}>{featured.name}</div>
          <div style={styles.eventMeta}>
            {formatDate(featured.date)} · {formatTime(featured.startTime)} – {formatTime(featured.endTime)}
          </div>
          {featured.venue?.name && <div style={styles.eventVenue}>📍 {featured.venue.name}</div>}
        </div>
      ) : (
        <div style={styles.eventCardLoading}>No scheduled events right now.</div>
      )}

      {/* Action menu */}
      <div style={styles.sectionTitle}>What would you like to do?</div>
      <div style={styles.grid}>
        {ACTIONS.map((a) => (
          <Link key={a.to} to={a.to} style={{ ...styles.card, borderTopColor: a.color }}>
            <div style={{ ...styles.cardIcon, color: a.color }}>{a.icon}</div>
            <div style={styles.cardTitle}>{a.title}</div>
            <div style={styles.cardDesc}>{a.desc}</div>
          </Link>
        ))}
      </div>

      <div style={styles.footerNote}>
        Need help? Find an organizer, or open the “🪘 Get live help” option from the login screen.
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '1.25rem', maxWidth: '760px', margin: '0 auto', width: '100%', boxSizing: 'border-box' as const },
  header: { marginBottom: '1rem' },
  title: { fontSize: '1.5rem', fontWeight: 800, color: '#0f2d1f', margin: 0 },
  subtitle: { fontSize: '0.9rem', color: '#6b7280', margin: '2px 0 0' },
  eventCard: { background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)', border: '2px solid #16a34a', borderRadius: '14px', padding: '1rem 1.25rem', marginBottom: '1.5rem' },
  eventCardLoading: { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '14px', padding: '1rem 1.25rem', marginBottom: '1.5rem', color: '#6b7280', fontSize: '0.9rem' },
  eventLabel: { fontSize: '0.8rem', fontWeight: 700, color: '#15803d', marginBottom: '0.35rem' },
  eventName: { fontSize: '1.2rem', fontWeight: 800, color: '#0f2d1f' },
  eventMeta: { fontSize: '0.85rem', color: '#374151', marginTop: '0.3rem' },
  eventVenue: { fontSize: '0.85rem', color: '#374151', marginTop: '0.2rem' },
  sectionTitle: { fontSize: '0.95rem', fontWeight: 700, color: '#0f2d1f', marginBottom: '0.75rem' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))', gap: '0.9rem' },
  card: { display: 'flex', flexDirection: 'column' as const, gap: '0.35rem', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderTop: '4px solid #1a6b3c', borderRadius: '14px', padding: '1.25rem', textDecoration: 'none', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', minHeight: '120px' },
  cardIcon: { fontSize: '1.9rem', lineHeight: 1 },
  cardTitle: { fontSize: '1.05rem', fontWeight: 700, color: '#0f2d1f' },
  cardDesc: { fontSize: '0.82rem', color: '#6b7280', lineHeight: 1.4 },
  footerNote: { marginTop: '1.5rem', fontSize: '0.8rem', color: '#9ca3af', textAlign: 'center' as const },
};
